import React, { useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import { openCommissionReceipt } from '../utils/commissionReceipt';
import logo from '../assets/logo.png';
import TargetWidget from '../components/TargetWidget';
import { fmtDate, fmtDateTime } from '../utils/dates';
import { PaymentPlanFields, PaymentPlanModal, RecordInstallmentModal, InstallmentProgress, InstallmentDuesPanel, emptyPlan, planError, rupees } from '../components/ServiceChargeInstallments';

// ─── CONSTANTS ────────────────────────────────────────────────────────────────
const PAYMENT_STATUSES = ['Unpaid', 'Paid', 'Partially Paid', 'Overdue', 'Cancelled'];
const STATUS_COLORS = {
  Unpaid:         'bg-yellow-100 text-yellow-800 border-yellow-300',
  Paid:           'bg-emerald-100 text-emerald-800 border-emerald-300',
  'Partially Paid': 'bg-blue-100 text-blue-800 border-blue-300',
  Overdue:        'bg-red-100 text-red-800 border-red-300',
  Cancelled:      'bg-gray-200 text-gray-500 border-gray-300',
};
const PAGE_SIZE = 10;

const emptyForm = () => ({
  client_name: '',
  client_contact: '',
  client_email: '',
  client_address: '',
  invoice_date: new Date().toISOString().slice(0, 10),
  due_date: '',
  service_description: '',
  amount: '',
  gst_percent: '0',
  notes: '',
});

const fmt = (n) =>
  new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 2 }).format(n || 0);

// ─── AI SUGGESTIONS ───────────────────────────────────────────────────────────
const AI_TEMPLATES = [
  {
    label: 'Placement Fee',
    description: 'Professional IT / Non-IT candidate placement service charge.',
    fill: { service_description: 'Professional Placement Service – Candidate sourcing, screening, shortlisting and final placement coordination for one confirmed hiring.', amount: '5000', gst_percent: '18' },
  },
  {
    label: 'Registration Fee',
    description: 'One-time member registration and profile activation fee.',
    fill: { service_description: 'Registration Fee – One-time enrollment and profile activation in Dani Job Club placement system.', amount: '500', gst_percent: '0' },
  },
  {
    label: 'Prime Membership',
    description: 'Annual prime membership with priority job matching.',
    fill: { service_description: 'Prime Membership Fee – Annual subscription with priority HR matching, direct recruiter access, and dedicated HR Assistant support.', amount: '2000', gst_percent: '0' },
  },
  {
    label: 'Consulting',
    description: 'HR consulting or career counselling session.',
    fill: { service_description: 'HR Consulting Session – Career guidance, resume review, interview preparation and job-match advisory.', amount: '1500', gst_percent: '18' },
  },
  {
    label: 'Corporate Hire',
    description: 'Bulk hiring or corporate staffing project fee.',
    fill: { service_description: 'Corporate Staffing Service – Bulk hiring project management, candidate pipeline sourcing and end-to-end recruitment coordination.', amount: '25000', gst_percent: '18' },
  },
];

// ─── PRINT / PDF INVOICE ──────────────────────────────────────────────────────
const printInvoice = (inv) => {
  const subTotal = parseFloat(inv.amount || 0).toFixed(2);
  const gstAmt   = parseFloat(inv.gst_amount || 0).toFixed(2);
  const total    = parseFloat(inv.total_amount || 0).toFixed(2);
  const statusBg    = inv.payment_status === 'Paid' ? '#d1fae5' : inv.payment_status === 'Cancelled' ? '#e5e7eb' : '#fef3c7';
  const statusColor = inv.payment_status === 'Paid' ? '#065f46' : inv.payment_status === 'Cancelled' ? '#6b7280' : '#92400e';
  const statusBorder= inv.payment_status === 'Paid' ? '#6ee7b7' : inv.payment_status === 'Cancelled' ? '#d1d5db' : '#fde68a';

  const logoSrc = `${window.location.origin}/assets/logo.png`;

  const html = `
<!DOCTYPE html>
<html lang="en">
<head>
  <meta charset="UTF-8"/>
  <meta name="viewport" content="width=device-width,initial-scale=1"/>
  <title>Invoice ${inv.invoice_number}</title>
  <style>
    @import url('https://fonts.googleapis.com/css2?family=Montserrat:wght@400;500;600;700;900&display=swap');
    *{box-sizing:border-box;margin:0;padding:0}
    body{font-family:'Montserrat',Arial,sans-serif;background:#eef2f7;padding:40px 20px;-webkit-print-color-adjust:exact;print-color-adjust:exact}
    .page{max-width:760px;margin:0 auto;background:#ffffff;border-radius:20px;overflow:hidden;box-shadow:0 8px 40px rgba(0,0,0,0.13)}

    /* ── HEADER ── */
    .header{background:linear-gradient(120deg,#111111 0%,#1f1f1f 55%,#2d2200 100%);padding:28px 40px;display:flex;align-items:center;justify-content:space-between;gap:20px}
    .header-logo{display:flex;align-items:center;gap:14px}
    .header-logo img{height:70px;width:70px;object-fit:contain;border-radius:50%;border:3px solid #FFD600;background:#fff;padding:3px;flex-shrink:0}
    .header-brand{display:flex;flex-direction:column;gap:2px}
    .header-brand .brand-name{font-size:18px;font-weight:900;color:#FFD600;letter-spacing:1.5px;line-height:1.1}
    .header-brand .brand-sub{font-size:10px;color:#aaa;font-weight:600;letter-spacing:2px;text-transform:uppercase}
    .header-right{text-align:right}
    .header-right .inv-label{font-size:32px;font-weight:900;color:#FFD600;letter-spacing:4px;line-height:1}
    .header-right .inv-year{font-size:11px;color:#888;margin-top:4px;font-weight:500;letter-spacing:1px}

    /* ── GOLDEN STRIPE ── */
    .stripe{height:5px;background:linear-gradient(90deg,#FFD600 0%,#f59e0b 50%,#FFD600 100%)}

    /* ── INVOICE META BAR ── */
    .inv-meta{display:flex;align-items:stretch;background:#fafaf8;border-bottom:1px solid #f0ece0}
    .meta-cell{flex:1;padding:18px 24px;border-right:1px solid #f0ece0}
    .meta-cell:last-child{border-right:none}
    .meta-cell .mc-label{font-size:10px;font-weight:700;letter-spacing:1.8px;color:#aaa;text-transform:uppercase;margin-bottom:5px}
    .meta-cell .mc-value{font-size:15px;font-weight:900;color:#111;line-height:1.2}
    .meta-cell .mc-sub{font-size:11px;color:#888;margin-top:3px;font-weight:500}
    .status-badge{display:inline-block;padding:4px 16px;border-radius:999px;font-size:12px;font-weight:700;background:${statusBg};color:${statusColor};border:1.5px solid ${statusBorder};letter-spacing:0.5px}

    /* ── BODY ── */
    .body{padding:32px 40px}

    /* ── FROM / TO ROW ── */
    .parties{display:flex;gap:24px;margin-bottom:28px}
    .party-box{flex:1;border-radius:14px;padding:18px 20px}
    .party-box.from{background:#1a1a1a;color:#fff}
    .party-box.to{background:#fffbf0;border:1.5px solid #fde68a}
    .party-box .pb-label{font-size:10px;font-weight:700;letter-spacing:2px;text-transform:uppercase;margin-bottom:8px}
    .party-box.from .pb-label{color:#FFD600}
    .party-box.to .pb-label{color:#b45309}
    .party-box .pb-name{font-size:16px;font-weight:900;margin-bottom:4px}
    .party-box.from .pb-name{color:#fff}
    .party-box.to .pb-name{color:#111}
    .party-box .pb-detail{font-size:12px;font-weight:500;line-height:1.7}
    .party-box.from .pb-detail{color:#bbb}
    .party-box.to .pb-detail{color:#555}

    /* ── SERVICES TABLE ── */
    .tbl-wrap{border-radius:14px;overflow:hidden;border:1.5px solid #e8e4d8;margin-bottom:24px}
    table{width:100%;border-collapse:collapse;font-size:13px}
    thead tr{background:#111}
    thead th{padding:12px 18px;text-align:left;font-weight:700;color:#FFD600;letter-spacing:0.8px;font-size:12px;text-transform:uppercase}
    thead th:last-child{text-align:right}
    tbody tr{border-bottom:1px solid #f0ece0}
    tbody tr:last-child{border-bottom:none}
    tbody tr:nth-child(even){background:#fafaf8}
    tbody td{padding:14px 18px;color:#333;vertical-align:top;line-height:1.5}
    tbody td:last-child{text-align:right;font-weight:700;white-space:nowrap}
    tbody td.desc{color:#444;font-weight:500}

    /* ── TOTALS ── */
    .totals-wrap{display:flex;justify-content:flex-end;margin-bottom:24px}
    .totals-box{background:#fafaf8;border:1.5px solid #e8e4d8;border-radius:14px;overflow:hidden;min-width:280px}
    .totals-row{display:flex;justify-content:space-between;align-items:center;padding:10px 20px;border-bottom:1px solid #f0ece0;font-size:13px;color:#555;font-weight:600}
    .totals-row:last-child{border-bottom:none;background:#111;padding:14px 20px}
    .totals-row:last-child .tr-label{color:#FFD600;font-size:14px;font-weight:900;letter-spacing:0.5px}
    .totals-row:last-child .tr-value{color:#fff;font-size:18px;font-weight:900}
    .tr-label{color:#666}
    .tr-value{color:#111;font-weight:800}

    /* ── NOTES ── */
    .notes-box{background:#f8f8f8;border-left:4px solid #FFD600;border-radius:0 10px 10px 0;padding:12px 16px;margin-bottom:24px;font-size:12.5px;color:#444;line-height:1.6}
    .notes-box strong{color:#111;font-weight:800}

    /* ── PAYMENT DETAILS ── */
    .payment-section{display:flex;gap:20px;margin-bottom:24px;align-items:flex-start}
    .bank-box{flex:1;background:#fffbf0;border:1.5px solid #fde68a;border-radius:14px;padding:18px 20px}
    .bank-box h4{font-size:10px;font-weight:900;letter-spacing:2px;color:#b45309;text-transform:uppercase;margin-bottom:12px}
    .bank-row{display:flex;font-size:12px;margin-bottom:7px;align-items:baseline}
    .bank-row .bk{color:#888;font-weight:600;min-width:115px;flex-shrink:0}
    .bank-row .bv{color:#111;font-weight:700}
    .qr-box{display:flex;flex-direction:column;align-items:center;gap:8px;background:#fff;border:1.5px solid #e8e4d8;border-radius:14px;padding:14px 18px}
    .qr-box img{width:96px;height:96px;object-fit:contain;border-radius:8px}
    .qr-box span{font-size:10px;color:#888;font-weight:600;text-align:center;max-width:100px}

    /* ── FOOTER ── */
    .footer{background:#111;padding:18px 40px;display:flex;align-items:center;justify-content:space-between;gap:16px}
    .footer-logo{display:flex;align-items:center;gap:10px}
    .footer-logo img{height:36px;width:36px;object-fit:contain;border-radius:50%;border:2px solid #FFD600;background:#fff;padding:2px}
    .footer-brand{font-size:12px;font-weight:800;color:#FFD600}
    .footer-brand span{display:block;font-size:9px;color:#888;font-weight:500;letter-spacing:1px}
    .footer-text{font-size:11px;color:#666;text-align:right;line-height:1.6}
    .footer-text strong{color:#FFD600}

    .cancelled-banner{background:#fef2f2;border:1.5px solid #fca5a5;border-radius:10px;padding:10px 16px;margin-bottom:20px;font-size:12.5px;color:#b91c1c;font-weight:700;text-align:center}

    @media print{
      body{padding:0;background:#fff}
      .page{box-shadow:none;border-radius:0}
    }
  </style>
</head>
<body>
<div class="page">

  <!-- HEADER -->
  <div class="header">
    <div class="header-logo">
      <img src="${logoSrc}" alt="Dani Job Club" onerror="this.style.display='none'" />
      <div class="header-brand">
        <div class="brand-name">DANIJOBCLUB.COM</div>
        <div class="brand-sub">Online Recruitment Partner</div>
        <div class="brand-sub" style="margin-top:2px;color:#777">LWIN Academy · Est. 2020</div>
      </div>
    </div>
    <div class="header-right">
      <div class="inv-label">INVOICE</div>
      <div class="inv-year">TAX INVOICE · ${new Date().getFullYear()}</div>
    </div>
  </div>

  <!-- GOLDEN STRIPE -->
  <div class="stripe"></div>

  <!-- INVOICE META BAR -->
  <div class="inv-meta">
    <div class="meta-cell">
      <div class="mc-label">Invoice No.</div>
      <div class="mc-value">${inv.invoice_number}</div>
    </div>
    <div class="meta-cell">
      <div class="mc-label">Invoice Date</div>
      <div class="mc-value">${fmtDate(inv.invoice_date)}</div>
      ${inv.due_date ? `<div class="mc-sub">Due: ${fmtDate(inv.due_date)}</div>` : ''}
    </div>
    <div class="meta-cell" style="flex:0.7;text-align:right">
      <div class="mc-label">Payment Status</div>
      <div style="margin-top:6px"><span class="status-badge">${inv.payment_status}</span></div>
    </div>
  </div>

  <!-- BODY -->
  <div class="body">

    ${inv.cancelled && inv.cancellation_reason ? `<div class="cancelled-banner">⚠ CANCELLED — ${inv.cancellation_reason}</div>` : ''}

    <!-- FROM / TO -->
    <div class="parties">
      <div class="party-box from">
        <div class="pb-label">From</div>
        <div class="pb-name">Dani Job Club</div>
        <div class="pb-detail">
          LWIN Academy<br/>
          Federal Bank UPI: lwin123@fbl<br/>
          Account No: 1256955555<br/>
          IFSC: FDRL00025 · Kulappully
        </div>
      </div>
      <div class="party-box to">
        <div class="pb-label">Bill To</div>
        <div class="pb-name">${inv.client_name}</div>
        <div class="pb-detail">
          ${inv.client_contact ? `📞 ${inv.client_contact}<br/>` : ''}
          ${inv.client_email ? `✉ ${inv.client_email}<br/>` : ''}
          ${inv.client_address ? `📍 ${inv.client_address}` : ''}
        </div>
      </div>
    </div>

    <!-- SERVICES TABLE -->
    <div class="tbl-wrap">
      <table>
        <thead>
          <tr>
            <th style="width:44px">#</th>
            <th>Description of Service</th>
            <th style="width:130px;text-align:right">Amount (INR)</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td>1</td>
            <td class="desc">${inv.service_description}</td>
            <td>₹${subTotal}</td>
          </tr>
        </tbody>
      </table>
    </div>

    <!-- TOTALS -->
    <div class="totals-wrap">
      <div class="totals-box">
        <div class="totals-row">
          <span class="tr-label">Sub Total</span>
          <span class="tr-value">₹${subTotal}</span>
        </div>
        ${inv.gst_percent > 0 ? `
        <div class="totals-row">
          <span class="tr-label">GST (${inv.gst_percent}%)</span>
          <span class="tr-value">₹${gstAmt}</span>
        </div>` : ''}
        <div class="totals-row">
          <span class="tr-label">Grand Total</span>
          <span class="tr-value">₹${total}</span>
        </div>
      </div>
    </div>

    ${inv.notes ? `<div class="notes-box"><strong>Notes:</strong> ${inv.notes}</div>` : ''}

    <!-- PAYMENT DETAILS -->
    <div class="payment-section">
      <div class="bank-box">
        <h4>Payment Details</h4>
        <div class="bank-row"><span class="bk">Account Name</span><span class="bv">Dani Job Club</span></div>
        <div class="bank-row"><span class="bk">Account Number</span><span class="bv">1256955555</span></div>
        <div class="bank-row"><span class="bk">IFSC Code</span><span class="bv">FDRL00025</span></div>
        <div class="bank-row"><span class="bk">Bank &amp; Branch</span><span class="bv">Federal Bank, Kulappully</span></div>
        <div class="bank-row"><span class="bk">UPI ID</span><span class="bv">lwin123@fbl</span></div>
      </div>
      <div class="qr-box">
        <img src="${window.location.origin}/assets/payment-qr.png" alt="Pay via UPI" onerror="this.style.display='none'" />
        <span>Scan &amp; Pay via any UPI app</span>
      </div>
    </div>

  </div><!-- /body -->

  <!-- FOOTER -->
  <div class="footer">
    <div class="footer-logo">
      <img src="${logoSrc}" alt="Dani Job Club" onerror="this.style.display='none'" />
      <div>
        <div class="footer-brand">DANI JOB CLUB <span>ONLINE RECRUITMENT PARTNER</span></div>
      </div>
    </div>
    <div class="footer-text">
      Thank you for your business!<br/>
      <strong>danijobclub.com</strong> · LWIN Academy
    </div>
  </div>

</div>
</body>
</html>`;

  const win = window.open('', '_blank');
  if (!win) return;
  win.document.write(html);
  win.document.close();
  setTimeout(() => win.print(), 700);
};

// ─── MAIN COMPONENT ───────────────────────────────────────────────────────────
const AccountantDashboard = ({ name, API_URL }) => {
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(false);
  const [tab, setTab] = useState('list'); // list | new | stats | requests
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [feedback, setFeedback] = useState('');
  const [search, setSearch] = useState('');
  const [filterStatus, setFilterStatus] = useState('All');
  const [page, setPage] = useState(1);
  const [reqPage, setReqPage] = useState(1);
  const [statsPage, setStatsPage] = useState(1);
  const [cancelModal, setCancelModal] = useState(null);
  const [cancelReason, setCancelReason] = useState('');
  const [cancelling, setCancelling] = useState(false);
  const [viewModal, setViewModal] = useState(null);
  const [aiOpen, setAiOpen] = useState(false);
  const [paymentRequests, setPaymentRequests] = useState([]);
  const [linkingRequestId, setLinkingRequestId] = useState(null);
  const [proformaModal, setProformaModal] = useState(null); // { req } — open proforma form for a request
  const [proformaForm, setProformaForm] = useState({ description: '', amount: '', gst_percent: '0', due_date: '', ...emptyPlan() });
  const [planModal, setPlanModal] = useState(null); // payment request whose installment plan is being edited
  const [recordModal, setRecordModal] = useState(null); // { inst, memberName } balance installment being recorded
  const [duesRefreshKey, setDuesRefreshKey] = useState(0);
  const [proformaSaving, setProformaSaving] = useState(false);
  const [proformaFeedback, setProformaFeedback] = useState('');
  const [proformaSentResult, setProformaSentResult] = useState(null); // updated PR after proforma sent
  const hasAutoSwitchedRef = useRef(false);
  // Inline "Convert Proforma → Invoice" modal state
  const [convertModal, setConvertModal] = useState(null); // { req } — payment_request being converted
  const [convertForm, setConvertForm] = useState({ invoice_date: new Date().toISOString().slice(0, 10), due_date: '', notes: '' });
  const [convertSaving, setConvertSaving] = useState(false);
  const [convertFeedback, setConvertFeedback] = useState('');
  const [convertedInvoice, setConvertedInvoice] = useState(null); // invoice created in conversion
  const formRef = useRef(null);

  // Partner commissions
  const [partnerCommissions, setPartnerCommissions] = useState([]);
  const [pcFilter, setPcFilter] = useState({ status: 'Pending', source: '', search: '' });
  const [pcLoading, setPcLoading] = useState(false);
  const [pcPayModal, setPcPayModal] = useState(null); // commission row being paid
  const [pcScreenshot, setPcScreenshot] = useState(null); // File
  const [pcPaying, setPcPaying] = useState(false);
  const [pcPayFeedback, setPcPayFeedback] = useState('');

  const token = () => localStorage.getItem('token');

  // ── Fetch invoices
  const fetchInvoices = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API_URL}/api/accountant/invoices`, {
        headers: { Authorization: `Bearer ${token()}` },
      });
      setInvoices(res.data || []);
    } catch {
      /* silent */
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchInvoices(); }, []); // eslint-disable-line

  // ── Fetch payment requests from HR
  const fetchPaymentRequests = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/accountant/payment-requests`, {
        headers: { Authorization: `Bearer ${token()}` },
      });
      setPaymentRequests(res.data || []);
    } catch { /* silent */ }
  };

  useEffect(() => { fetchPaymentRequests(); }, []); // eslint-disable-line

  const fetchPartnerCommissions = async () => {
    setPcLoading(true);
    try {
      const res = await axios.get(`${API_URL}/api/accountant/partner-commissions`, { headers: { Authorization: `Bearer ${token()}` } });
      setPartnerCommissions(res.data || []);
    } catch { setPartnerCommissions([]); }
    finally { setPcLoading(false); }
  };

  useEffect(() => { fetchPartnerCommissions(); }, []); // eslint-disable-line

  const handleMarkPcPaid = async () => {
    if (!pcPayModal) return;
    setPcPaying(true);
    setPcPayFeedback('');
    try {
      const fd = new FormData();
      if (pcScreenshot) fd.append('transaction_screenshot', pcScreenshot);
      await axios.put(`${API_URL}/api/accountant/partner-commissions/${pcPayModal.id}/mark-paid`, fd, {
        headers: { Authorization: `Bearer ${token()}`, 'Content-Type': 'multipart/form-data' },
      });
      setPartnerCommissions((prev) => prev.map((c) => c.id === pcPayModal.id ? { ...c, status: 'Paid', paid_at: new Date().toISOString().slice(0, 10) } : c));
      setPcPayModal(null);
      setPcScreenshot(null);
    } catch (err) {
      setPcPayFeedback(err?.response?.data?.error || 'Failed to mark as paid.');
    } finally {
      setPcPaying(false);
    }
  };

  // ── Auto-switch to requests tab when pending requests arrive (once)
  useEffect(() => {
    if (!hasAutoSwitchedRef.current && paymentRequests.some((r) => r.status === 'Pending')) {
      setTab('requests');
      hasAutoSwitchedRef.current = true;
    }
  }, [paymentRequests]); // eslint-disable-line

  // ── Computed amounts
  const gstAmt = useMemo(() => {
    const a = parseFloat(form.amount) || 0;
    const g = parseFloat(form.gst_percent) || 0;
    return a * (g / 100);
  }, [form.amount, form.gst_percent]);

  const totalAmt = useMemo(() => (parseFloat(form.amount) || 0) + gstAmt, [form.amount, gstAmt]);

  // ── Stats
  const stats = useMemo(() => {
    const active = invoices.filter((i) => !i.cancelled);
    return {
      total: invoices.length,
      paid: invoices.filter((i) => i.payment_status === 'Paid').length,
      unpaid: invoices.filter((i) => i.payment_status === 'Unpaid').length,
      overdue: invoices.filter((i) => i.payment_status === 'Overdue').length,
      cancelled: invoices.filter((i) => i.cancelled).length,
      revenue: active.filter((i) => i.payment_status === 'Paid').reduce((s, i) => s + parseFloat(i.total_amount || 0), 0),
      outstanding: active.filter((i) => ['Unpaid', 'Partially Paid', 'Overdue'].includes(i.payment_status)).reduce((s, i) => s + parseFloat(i.total_amount || 0), 0),
    };
  }, [invoices]);

  // ── Filtered + paginated list
  const filtered = useMemo(() => {
    let list = invoices;
    if (filterStatus !== 'All') {
      if (filterStatus === 'Cancelled') list = list.filter((i) => i.cancelled);
      else list = list.filter((i) => !i.cancelled && i.payment_status === filterStatus);
    }
    if (search.trim()) {
      const q = search.toLowerCase();
      list = list.filter((i) =>
        i.invoice_number?.toLowerCase().includes(q) ||
        i.client_name?.toLowerCase().includes(q) ||
        i.service_description?.toLowerCase().includes(q)
      );
    }
    return list;
  }, [invoices, filterStatus, search]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const safePage = Math.min(Math.max(page, 1), totalPages);
  const paged = filtered.slice((safePage - 1) * PAGE_SIZE, safePage * PAGE_SIZE);

  // ── Handlers
  const handleField = (e) => setForm((f) => ({ ...f, [e.target.name]: e.target.value }));

  const [pendingLinkRequestId, setPendingLinkRequestId] = useState(null);

  const handleCreate = async (e) => {
    e.preventDefault();
    setSaving(true);
    setFeedback('');
    try {
      const res = await axios.post(`${API_URL}/api/accountant/invoices`, form, {
        headers: { Authorization: `Bearer ${token()}` },
      });
      const newInvoice = res.data;
      setInvoices((prev) => [newInvoice, ...prev]);
      // If created from a payment request, link it back + notify HR
      if (pendingLinkRequestId) {
        try {
          await axios.patch(
            `${API_URL}/api/accountant/payment-requests/${pendingLinkRequestId}/invoice-created`,
            { invoice_id: newInvoice.id, invoice_number: newInvoice.invoice_number },
            { headers: { Authorization: `Bearer ${token()}` } }
          );
          fetchPaymentRequests();
        } catch { /* non-fatal */ }
        setPendingLinkRequestId(null);
      }
      setForm(emptyForm());
      setFeedback('✅ Invoice created successfully!');
      setTab('list');
    } catch (err) {
      setFeedback(`❌ ${err?.response?.data?.error || 'Failed to create invoice.'}`);
    } finally {
      setSaving(false);
    }
  };

  const handleStatusChange = async (inv, status) => {
    try {
      const res = await axios.patch(
        `${API_URL}/api/accountant/invoices/${inv.id}/status`,
        { payment_status: status },
        { headers: { Authorization: `Bearer ${token()}` } }
      );
      setInvoices((prev) => prev.map((i) => (i.id === inv.id ? res.data : i)));
    } catch (err) {
      alert(err?.response?.data?.error || 'Failed to update status.');
    }
  };

  const handleCancel = async () => {
    if (!cancelModal) return;
    setCancelling(true);
    try {
      const res = await axios.patch(
        `${API_URL}/api/accountant/invoices/${cancelModal.id}/cancel`,
        { cancellation_reason: cancelReason },
        { headers: { Authorization: `Bearer ${token()}` } }
      );
      setInvoices((prev) => prev.map((i) => (i.id === cancelModal.id ? res.data : i)));
      setCancelModal(null);
      setCancelReason('');
    } catch (err) {
      alert(err?.response?.data?.error || 'Failed to cancel invoice.');
    } finally {
      setCancelling(false);
    }
  };

  const applyAiTemplate = (tpl) => {
    setForm((f) => ({ ...f, ...tpl.fill }));
    setPendingLinkRequestId(null);
    setAiOpen(false);
    setTab('new');
    setTimeout(() => formRef.current?.scrollIntoView({ behavior: 'smooth' }), 100);
  };

  const applyFromRequest = (req) => {
    setForm({
      ...emptyForm(),
      client_name: req.member_name || '',
      client_contact: req.contact_number || '',
      client_address: [req.place, req.district].filter(Boolean).join(', '),
      invoice_date: new Date().toISOString().slice(0, 10),
      service_description: `${req.payment_type} – ${req.member_name}${req.member_id ? ` (ID: ${req.member_id})` : ''}`,
      amount: String(req.paid_amount || ''),
      gst_percent: '0',
    });
    setPendingLinkRequestId(req.id);
    setTab('new');
    setTimeout(() => formRef.current?.scrollIntoView({ behavior: 'smooth' }), 100);
  };

  const handleOpenProformaModal = (req) => {
    setProformaForm({
      description: `Service Charge – Placement of ${req.member_name}${req.member_id ? ` (ID: ${req.member_id})` : ''}`,
      amount: String(req.paid_amount || ''),
      gst_percent: '0',
      due_date: '',
      ...emptyPlan(),
    });
    setProformaFeedback('');
    setProformaSentResult(null);
    setProformaModal(req);
  };

  const handleSendProforma = async () => {
    if (!proformaModal) return;
    const proformaTotal = Number(proformaForm.amount) * (1 + Number(proformaForm.gst_percent) / 100);
    const planProblem = planError(proformaForm, proformaTotal);
    if (planProblem) { setProformaFeedback(`❌ ${planProblem}`); return; }
    setProformaSaving(true);
    setProformaFeedback('');
    try {
      const res = await axios.patch(
        `${API_URL}/api/accountant/payment-requests/${proformaModal.id}/send-proforma`,
        proformaForm,
        { headers: { Authorization: `Bearer ${token()}` } }
      );
      setProformaSentResult(res.data); // keep modal open to show WA send button
      setProformaFeedback('✅ Proforma created! Send it directly to the member below.');
      fetchPaymentRequests();
      setDuesRefreshKey((k) => k + 1);
    } catch (err) {
      setProformaFeedback(`❌ ${err?.response?.data?.error || 'Failed to send proforma.'}`);
    } finally {
      setProformaSaving(false);
    }
  };

  const handleOpenConvertModal = (req) => {
    const pd = (() => { try { return req.proforma_data ? (typeof req.proforma_data === 'string' ? JSON.parse(req.proforma_data) : req.proforma_data) : null; } catch (_) { return null; } })();
    setConvertForm({
      invoice_date: new Date().toISOString().slice(0, 10),
      due_date: pd?.due_date || '',
      notes: '',
    });
    setConvertFeedback('');
    setConvertedInvoice(null);
    setConvertModal(req);
  };

  const handleConvertToInvoice = async () => {
    if (!convertModal) return;
    setConvertSaving(true);
    setConvertFeedback('');
    try {
      const res = await axios.post(
        `${API_URL}/api/accountant/payment-requests/${convertModal.id}/convert-to-invoice`,
        convertForm,
        { headers: { Authorization: `Bearer ${token()}` } }
      );
      setConvertedInvoice(res.data.invoice);
      setConvertFeedback(`✅ Invoice ${res.data.invoice.invoice_number} created successfully!`);
      fetchInvoices();
      fetchPaymentRequests();
    } catch (err) {
      setConvertFeedback(`❌ ${err?.response?.data?.error || 'Failed to create invoice.'}`);
    } finally {
      setConvertSaving(false);
    }
  };

  // ── Pagination for requests tab
  const REQ_PAGE_SIZE = 10;
  const totalReqPages = Math.max(1, Math.ceil(paymentRequests.length / REQ_PAGE_SIZE));
  const safeReqPage = Math.min(Math.max(reqPage, 1), totalReqPages);
  const pagedReqs = paymentRequests.slice((safeReqPage - 1) * REQ_PAGE_SIZE, safeReqPage * REQ_PAGE_SIZE);

  // ── Pagination for stats tab paid invoices
  const STATS_PAGE_SIZE = 10;
  const paidInvoices = invoices.filter((i) => i.payment_status === 'Paid');
  const totalStatsPages = Math.max(1, Math.ceil(paidInvoices.length / STATS_PAGE_SIZE));
  const safeStatsPage = Math.min(Math.max(statsPage, 1), totalStatsPages);
  const pagedPaid = paidInvoices.slice((safeStatsPage - 1) * STATS_PAGE_SIZE, safeStatsPage * STATS_PAGE_SIZE);

  // ── Render
  return (
    <div className="min-h-screen bg-[radial-gradient(circle_at_top_left,rgba(250,204,21,0.22),transparent_18%),linear-gradient(180deg,#fffdf8_0%,#f8fafc_50%,#fffaf0_100%)] px-2 py-6 font-official text-secondary md:px-4 xl:px-6">
      <div className="mx-auto w-full max-w-[1600px] overflow-hidden rounded-[30px] border border-yellow-200 bg-white shadow-[0_24px_70px_rgba(15,23,42,0.08)]">

        {/* ── HEADER ──────────────────────────────────────────────────────── */}
        <div className="bg-[radial-gradient(circle_at_top_left,rgba(250,204,21,0.26),transparent_24%),linear-gradient(135deg,#090909_0%,#1c1c1c_55%,#5b4703_100%)] px-5 py-4 text-white md:px-8">
          <div className="flex flex-wrap items-center justify-between gap-4">
            <div className="flex items-center gap-3">
              <img src={logo} alt="Dani Job Club" className="h-10 w-auto" />
              <div>
                <p className="text-[10px] font-extrabold uppercase tracking-[0.34em] text-yellow-300">Accountant Desk</p>
                <h1 className="mt-0.5 text-base font-black leading-none">{name}</h1>
              </div>
            </div>
            <div className="flex flex-wrap items-center gap-2">
              {[
                { label: 'Total', val: stats.total, c: 'text-yellow-300' },
                { label: 'Paid', val: stats.paid, c: 'text-emerald-400' },
                { label: 'Unpaid', val: stats.unpaid, c: 'text-red-400' },
                { label: 'Overdue', val: stats.overdue, c: 'text-orange-400' },
                { label: 'Revenue', val: fmt(stats.revenue), c: 'text-emerald-300' },
                { label: 'Outstanding', val: fmt(stats.outstanding), c: 'text-yellow-200' },
              ].map((s) => (
                <div key={s.label} className="rounded-xl border border-white/10 bg-white/10 px-3 py-1.5 backdrop-blur-sm text-center">
                  <p className={`text-xs font-black ${s.c}`}>{s.val}</p>
                  <p className="text-[9px] font-bold uppercase tracking-wide text-gray-400">{s.label}</p>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* ── TABS ────────────────────────────────────────────────────────── */}
        <div className="border-b border-gray-100 px-4 py-3 md:px-6">
          <div className="flex flex-wrap items-center gap-2">
            {[
              { key: 'list', icon: '📋', label: 'Invoices' },
              { key: 'new', icon: '✚', label: 'New Invoice' },
              { key: 'stats', icon: '📊', label: 'Statistics' },
              { key: 'requests', icon: '📥', label: 'Payment Requests', badge: paymentRequests.filter((r) => r.status === 'Pending').length },
              { key: 'partner-commissions', icon: '🤝', label: 'Partner Commissions', badge: partnerCommissions.filter((c) => c.status === 'Pending').length },
            ].map((t) => (
              <button
                key={t.key}
                type="button"
                onClick={() => { setTab(t.key); setReqPage(1); setStatsPage(1); }}
                className={`flex items-center gap-1.5 rounded-xl border px-4 py-2 text-sm font-bold transition ${
                  tab === t.key
                    ? 'border-black bg-black text-white'
                    : 'border-gray-200 bg-white text-black hover:border-yellow-400'
                }`}
              >
                <span>{t.icon}</span>
                <span>{t.label}</span>
                {t.badge > 0 && (
                  <span className={`rounded-full px-1.5 py-0.5 text-[10px] font-black ${tab === t.key ? 'bg-yellow-400 text-black' : 'bg-red-500 text-white'}`}>{t.badge}</span>
                )}
              </button>
            ))}
            <div className="ml-auto flex gap-2">
              <button
                type="button"
                onClick={() => { fetchInvoices(); fetchPaymentRequests(); fetchPartnerCommissions(); }}
                className="rounded-xl bg-yellow-400 px-3 py-2 text-sm font-bold text-black hover:bg-yellow-500"
              >
                ↻ Refresh
              </button>
            </div>
          </div>
        </div>

        <div className="px-4 py-4 md:px-6 space-y-4">
          <TargetWidget API_URL={API_URL} />

          {/* ── FEEDBACK ──────────────────────────────────────────────────── */}
          {feedback && (
            <div className={`rounded-2xl border px-4 py-3 text-sm font-semibold ${
              feedback.startsWith('✅')
                ? 'border-emerald-200 bg-emerald-50 text-emerald-700'
                : 'border-red-200 bg-red-50 text-red-700'
            }`}>
              {feedback}
            </div>
          )}

          {/* ══════════════════════════════════════════════════════════════
              TAB: INVOICE LIST
          ══════════════════════════════════════════════════════════════ */}
          {tab === 'list' && (
            <div className="space-y-4">
              {/* Filter row */}
              <div className="rounded-[24px] border border-yellow-200 bg-[linear-gradient(135deg,#fffdf7_0%,#fff8e6_50%,#ffffff_100%)] p-4">
                <div className="flex flex-wrap gap-3 items-center">
                  <input
                    type="text"
                    placeholder="Search by name, invoice no, service…"
                    value={search}
                    onChange={(e) => { setSearch(e.target.value); setPage(1); }}
                    className="flex-1 min-w-[200px] rounded-2xl border border-gray-300 bg-white px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-yellow-400"
                  />
                  <div className="flex gap-1.5 flex-wrap">
                    {['All', 'Unpaid', 'Paid', 'Partially Paid', 'Overdue', 'Cancelled'].map((s) => (
                      <button
                        key={s}
                        type="button"
                        onClick={() => { setFilterStatus(s); setPage(1); }}
                        className={`rounded-full px-3 py-1.5 text-xs font-bold border transition ${
                          filterStatus === s
                            ? 'bg-black text-yellow-300 border-black'
                            : 'bg-white text-gray-600 border-gray-300 hover:border-yellow-400'
                        }`}
                      >
                        {s}
                      </button>
                    ))}
                  </div>
                </div>
              </div>

              {/* Table */}
              <div className="rounded-[24px] overflow-hidden border border-gray-200 shadow-sm">
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="bg-gradient-to-r from-yellow-400 to-yellow-300">
                        {['Invoice No', 'Date', 'Client', 'Description', 'Amount', 'GST', 'Total', 'Status', 'Actions'].map((h) => (
                          <th key={h} className="px-4 py-3 text-left text-xs font-black uppercase tracking-wider text-black">{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {loading ? (
                        <tr><td colSpan={9} className="py-12 text-center text-gray-400">Loading invoices…</td></tr>
                      ) : paged.length === 0 ? (
                        <tr>
                          <td colSpan={9}>
                            <div className="py-16 text-center">
                              <p className="text-4xl mb-2">🧾</p>
                              <p className="text-gray-400 font-semibold">No invoices found.</p>
                            </div>
                          </td>
                        </tr>
                      ) : paged.map((inv, idx) => (
                        <tr
                          key={inv.id}
                          className={`border-b border-gray-100 transition-colors hover:bg-yellow-50/60 ${
                            inv.cancelled ? 'opacity-50 bg-gray-50' : idx % 2 === 0 ? 'bg-white' : 'bg-gray-50/40'
                          }`}
                        >
                          <td className="px-4 py-3 font-mono text-yellow-700 font-black text-xs whitespace-nowrap">{inv.invoice_number}</td>
                          <td className="px-4 py-3 text-gray-500 whitespace-nowrap text-xs">{fmtDate(inv.invoice_date)}</td>
                          <td className="px-4 py-3">
                            <p className="font-black text-gray-900">{inv.client_name}</p>
                            {inv.client_contact && <p className="text-xs text-gray-400 mt-0.5">{inv.client_contact}</p>}
                          </td>
                          <td className="px-4 py-3 text-gray-500 text-xs max-w-[180px] truncate" title={inv.service_description}>{inv.service_description}</td>
                          <td className="px-4 py-3 text-gray-700 text-xs font-semibold whitespace-nowrap">{fmt(inv.amount)}</td>
                          <td className="px-4 py-3 text-gray-400 text-xs whitespace-nowrap">{inv.gst_percent > 0 ? `${inv.gst_percent}%` : '—'}</td>
                          <td className="px-4 py-3 whitespace-nowrap">
                            <span className="font-black text-gray-900 text-sm">{fmt(inv.total_amount)}</span>
                          </td>
                          <td className="px-4 py-3">
                            {inv.cancelled ? (
                              <span className="rounded-full border border-gray-300 bg-gray-100 px-2.5 py-1 text-xs font-bold text-gray-500">Cancelled</span>
                            ) : (
                              <select
                                value={inv.payment_status}
                                onChange={(e) => handleStatusChange(inv, e.target.value)}
                                className={`rounded-full border px-2.5 py-1 text-xs font-bold cursor-pointer focus:outline-none ${STATUS_COLORS[inv.payment_status] || 'bg-gray-100 text-gray-600 border-gray-300'}`}
                              >
                                {['Unpaid', 'Paid', 'Partially Paid', 'Overdue'].map((s) => (
                                  <option key={s} value={s}>{s}</option>
                                ))}
                              </select>
                            )}
                          </td>
                          <td className="px-4 py-3">
                            <div className="flex gap-1.5 flex-wrap">
                              <button
                                type="button"
                                onClick={() => setViewModal(inv)}
                                className="rounded-xl border border-gray-300 bg-white px-2.5 py-1 text-xs font-bold text-gray-700 hover:bg-gray-50 hover:border-yellow-400 whitespace-nowrap"
                              >
                                View
                              </button>
                              <button
                                type="button"
                                onClick={() => printInvoice(inv)}
                                className="rounded-xl border border-emerald-200 bg-emerald-50 px-2.5 py-1 text-xs font-bold text-emerald-700 hover:bg-emerald-100 whitespace-nowrap"
                              >
                                Print
                              </button>
                              {!inv.cancelled && (
                                <button
                                  type="button"
                                  onClick={() => { setCancelModal(inv); setCancelReason(''); }}
                                  className="rounded-xl border border-red-200 bg-red-50 px-2.5 py-1 text-xs font-bold text-red-600 hover:bg-red-100 whitespace-nowrap"
                                >
                                  Cancel
                                </button>
                              )}
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>

              {/* Pagination */}
              <div className="flex items-center justify-between">
                <span className="text-xs font-semibold text-gray-400">Page {safePage} of {totalPages} · {filtered.length} invoices</span>
                <div className="flex gap-2">
                  <button
                    type="button"
                    disabled={safePage <= 1}
                    onClick={() => setPage((p) => p - 1)}
                    className="rounded-xl border border-gray-300 bg-white px-4 py-1.5 text-xs font-bold text-gray-700 hover:bg-gray-50 disabled:opacity-40"
                  >
                    ← Prev
                  </button>
                  <button
                    type="button"
                    disabled={safePage >= totalPages}
                    onClick={() => setPage((p) => p + 1)}
                    className="rounded-xl border border-gray-300 bg-white px-4 py-1.5 text-xs font-bold text-gray-700 hover:bg-gray-50 disabled:opacity-40"
                  >
                    Next →
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* ══════════════════════════════════════════════════════════════
              TAB: NEW INVOICE
          ══════════════════════════════════════════════════════════════ */}
          {tab === 'new' && (
            <div ref={formRef} className="space-y-5">
              {/* AI Tool Banner */}
              <div className="rounded-[24px] border border-yellow-200 bg-[linear-gradient(135deg,#fffdf7_0%,#fff8e6_50%,#ffffff_100%)] p-4 flex items-center justify-between gap-4">
                <div>
                  <p className="text-xs font-black uppercase tracking-[0.2em] text-yellow-700">✨ AI Invoice Assistant</p>
                  <p className="text-sm text-gray-600 mt-0.5">Pick a service template and auto-fill the form instantly.</p>
                </div>
                <button
                  type="button"
                  onClick={() => setAiOpen((o) => !o)}
                  className={`shrink-0 rounded-full px-5 py-2 text-sm font-black shadow transition ${
                    aiOpen ? 'bg-gray-900 text-yellow-300 hover:bg-black' : 'bg-yellow-400 text-black hover:bg-yellow-300'
                  }`}
                >
                  {aiOpen ? '✕ Close' : '✨ Open Templates'}
                </button>
              </div>

              {/* AI Template Cards */}
              {aiOpen && (
                <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5">
                  {AI_TEMPLATES.map((tpl) => (
                    <button
                      key={tpl.label}
                      type="button"
                      onClick={() => applyAiTemplate(tpl)}
                      className="group rounded-[20px] border border-yellow-200 bg-white p-4 text-left shadow-sm hover:border-yellow-400 hover:shadow-md transition-all"
                    >
                      <p className="font-black text-gray-900 text-sm group-hover:text-black">{tpl.label}</p>
                      <p className="mt-1.5 text-xs text-gray-500 leading-relaxed">{tpl.description}</p>
                      <div className="mt-3 flex items-center justify-between">
                        <p className="text-sm font-black text-yellow-700">₹{parseFloat(tpl.fill.amount).toLocaleString('en-IN')}</p>
                        {tpl.fill.gst_percent > 0 && <span className="text-[10px] font-bold text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-full px-2 py-0.5">+{tpl.fill.gst_percent}% GST</span>}
                      </div>
                      <span className="mt-2 inline-block text-[10px] font-black text-yellow-600 uppercase tracking-widest group-hover:text-yellow-800">→ Use Template</span>
                    </button>
                  ))}
                </div>
              )}

              {/* Form */}
              <form
                onSubmit={handleCreate}
                className="rounded-[24px] border border-gray-200 bg-white p-6 shadow-sm space-y-6"
              >
                <div className="flex items-center justify-between">
                  <h2 className="text-lg font-black text-gray-900">New Invoice</h2>
                  <span className="rounded-full bg-yellow-100 border border-yellow-300 px-3 py-1 text-xs font-black text-yellow-800">Accountant Desk</span>
                </div>

                {/* Step 1: Client Details */}
                <fieldset>
                  <legend className="flex items-center gap-2 text-xs font-black uppercase tracking-[0.2em] text-yellow-700 mb-3">
                    <span className="inline-flex h-5 w-5 items-center justify-center rounded-full bg-yellow-400 text-black text-[10px] font-black">1</span>
                    Client Details
                  </legend>
                  <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                    {[
                      { name: 'client_name', label: 'Client Name', req: true, type: 'text' },
                      { name: 'client_contact', label: 'Contact Number', req: false, type: 'text' },
                      { name: 'client_email', label: 'Email Address', req: false, type: 'email' },
                      { name: 'client_address', label: 'Address', req: false, type: 'text' },
                    ].map((f) => (
                      <label key={f.name} className="block text-xs font-semibold text-gray-600 uppercase tracking-wider">
                        {f.label}{f.req && <span className="ml-1 text-red-500">*</span>}
                        <input
                          name={f.name}
                          value={form[f.name]}
                          onChange={handleField}
                          required={f.req}
                          type={f.type}
                          className="mt-1 w-full rounded-xl border border-gray-300 bg-gray-50 px-3.5 py-2.5 text-sm text-gray-900 font-normal focus:outline-none focus:ring-2 focus:ring-yellow-400 focus:bg-white transition"
                        />
                      </label>
                    ))}
                  </div>
                </fieldset>

                {/* Step 2: Invoice Dates */}
                <fieldset>
                  <legend className="flex items-center gap-2 text-xs font-black uppercase tracking-[0.2em] text-yellow-700 mb-3">
                    <span className="inline-flex h-5 w-5 items-center justify-center rounded-full bg-yellow-400 text-black text-[10px] font-black">2</span>
                    Invoice Dates
                  </legend>
                  <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                    <label className="block text-xs font-semibold text-gray-600 uppercase tracking-wider">
                      Invoice Date <span className="text-red-500">*</span>
                      <input name="invoice_date" value={form.invoice_date} onChange={handleField} type="date" required
                        className="mt-1 w-full rounded-xl border border-gray-300 bg-gray-50 px-3.5 py-2.5 text-sm text-gray-900 focus:outline-none focus:ring-2 focus:ring-yellow-400 focus:bg-white transition" />
                    </label>
                    <label className="block text-xs font-semibold text-gray-600 uppercase tracking-wider">
                      Due Date
                      <input name="due_date" value={form.due_date} onChange={handleField} type="date"
                        className="mt-1 w-full rounded-xl border border-gray-300 bg-gray-50 px-3.5 py-2.5 text-sm text-gray-900 focus:outline-none focus:ring-2 focus:ring-yellow-400 focus:bg-white transition" />
                    </label>
                  </div>
                </fieldset>

                {/* Step 3: Service & Amount */}
                <fieldset>
                  <legend className="flex items-center gap-2 text-xs font-black uppercase tracking-[0.2em] text-yellow-700 mb-3">
                    <span className="inline-flex h-5 w-5 items-center justify-center rounded-full bg-yellow-400 text-black text-[10px] font-black">3</span>
                    Service &amp; Amount
                  </legend>
                  <div className="space-y-4">
                    <label className="block text-xs font-semibold text-gray-600 uppercase tracking-wider">
                      Service Description <span className="text-red-500">*</span>
                      <textarea name="service_description" value={form.service_description} onChange={handleField} required rows={3}
                        className="mt-1 w-full rounded-xl border border-gray-300 bg-gray-50 px-3.5 py-2.5 text-sm text-gray-900 font-normal focus:outline-none focus:ring-2 focus:ring-yellow-400 focus:bg-white transition resize-none" />
                    </label>
                    <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
                      <label className="block text-xs font-semibold text-gray-600 uppercase tracking-wider">
                        Amount (₹) <span className="text-red-500">*</span>
                        <input name="amount" value={form.amount} onChange={handleField} type="number" min="0" step="0.01" required
                          className="mt-1 w-full rounded-xl border border-gray-300 bg-gray-50 px-3.5 py-2.5 text-sm font-normal focus:outline-none focus:ring-2 focus:ring-yellow-400 focus:bg-white transition" />
                      </label>
                      <label className="block text-xs font-semibold text-gray-600 uppercase tracking-wider">
                        GST %
                        <input name="gst_percent" value={form.gst_percent} onChange={handleField} type="number" min="0" max="100" step="0.01"
                          className="mt-1 w-full rounded-xl border border-gray-300 bg-gray-50 px-3.5 py-2.5 text-sm font-normal focus:outline-none focus:ring-2 focus:ring-yellow-400 focus:bg-white transition" />
                      </label>
                      {/* Live Total Preview */}
                      <div className="rounded-[20px] border-2 border-yellow-400 bg-yellow-50 px-4 py-3 flex flex-col justify-center">
                        <p className="text-[10px] font-black uppercase tracking-[0.2em] text-yellow-700">Invoice Total</p>
                        <p className="text-2xl font-black text-gray-900 mt-0.5">{fmt(totalAmt)}</p>
                        {gstAmt > 0 && <p className="text-xs text-yellow-700 mt-0.5">incl. GST {fmt(gstAmt)}</p>}
                      </div>
                    </div>
                    <label className="block text-xs font-semibold text-gray-600 uppercase tracking-wider">
                      Notes (optional)
                      <textarea name="notes" value={form.notes} onChange={handleField} rows={2}
                        className="mt-1 w-full rounded-xl border border-gray-300 bg-gray-50 px-3.5 py-2.5 text-sm font-normal focus:outline-none focus:ring-2 focus:ring-yellow-400 focus:bg-white transition resize-none" />
                    </label>
                  </div>
                </fieldset>

                <div className="flex gap-3 pt-1 border-t border-gray-100">
                  <button
                    type="submit"
                    disabled={saving}
                    className="rounded-full bg-black px-8 py-2.5 font-black text-yellow-300 hover:bg-gray-900 shadow disabled:opacity-60 text-sm"
                  >
                    {saving ? 'Creating…' : '✚ Create Invoice'}
                  </button>
                  <button
                    type="button"
                    onClick={() => { setForm(emptyForm()); setFeedback(''); }}
                    className="rounded-full border border-gray-300 bg-white px-6 py-2.5 font-bold text-gray-700 hover:bg-gray-50 text-sm"
                  >
                    Reset
                  </button>
                </div>
              </form>

              {/* Bank / QR Details */}
              <div className="rounded-[24px] border border-yellow-200 bg-[linear-gradient(135deg,#fffdf7_0%,#fff8e6_80%,#ffffff_100%)] p-5">
                <p className="text-xs font-black uppercase tracking-[0.2em] text-yellow-700 mb-4">💳 Payment Collection Details</p>
                <div className="flex flex-col md:flex-row gap-6 items-start">
                  <div className="shrink-0 flex flex-col items-center">
                    <div className="rounded-2xl border-2 border-yellow-300 bg-white p-2 shadow-sm">
                      <img
                        src={`${process.env.PUBLIC_URL}/assets/payment-qr.png`}
                        alt="QR"
                        className="w-28 h-28 object-contain"
                        onError={(e) => { e.target.style.display = 'none'; }}
                      />
                    </div>
                    <p className="text-[10px] font-bold text-yellow-700 mt-2">Scan &amp; Pay</p>
                  </div>
                  <div className="flex-1 space-y-2">
                    {[
                      ['UPI ID', 'lwin123@fbl'],
                      ['Account Name', 'Dani Job Club'],
                      ['Account No', '1256955555'],
                      ['IFSC', 'FDRL00025'],
                      ['Bank', 'Federal Bank, Kulappully'],
                    ].map(([k, v]) => (
                      <div key={k} className="flex items-center gap-3 rounded-xl border border-yellow-100 bg-white px-4 py-2.5">
                        <span className="text-xs font-black text-gray-400 uppercase tracking-wider w-28 shrink-0">{k}</span>
                        <span className="font-black text-gray-900 text-sm">{v}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* ══════════════════════════════════════════════════════════════
              TAB: PAYMENT REQUESTS
          ══════════════════════════════════════════════════════════════ */}
          {tab === 'requests' && (
            <div className="space-y-4 p-4 md:p-6">
              {/* Header */}
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="text-lg font-black text-gray-900">Payment Requests from HR</h3>
                  <p className="text-sm text-gray-500 mt-0.5">
                    {paymentRequests.filter((r) => r.status === 'Pending').length} proforma pending · {paymentRequests.filter((r) => r.status === 'Invoice Ready').length} proforma sent · {paymentRequests.filter((r) => r.status === 'Partially Paid').length} part-paid · {paymentRequests.filter((r) => r.status === 'Paid').length} paid
                  </p>
                </div>
                <button type="button" onClick={() => { fetchPaymentRequests(); setDuesRefreshKey((k) => k + 1); }} className="rounded-xl bg-yellow-400 px-3 py-2 text-sm font-bold text-black hover:bg-yellow-500">↻ Refresh</button>
              </div>

              <InstallmentDuesPanel API_URL={API_URL} endpoint="/api/accountant/installments/dues" canRecord refreshKey={duesRefreshKey} onChanged={fetchPaymentRequests} />

              {paymentRequests.length === 0 ? (
                <div className="rounded-2xl border-2 border-dashed border-gray-200 py-16 text-center text-gray-400">
                  <div className="text-4xl mb-3">📭</div>
                  <p className="text-base font-semibold">No payment requests yet</p>
                  <p className="text-sm mt-1">HR Assistant will send proforma requests here</p>
                </div>
              ) : (
                <div className="space-y-3">
                <div className="overflow-x-auto rounded-2xl border border-gray-200 shadow-sm">
                  <table className="min-w-full divide-y divide-gray-100 text-sm">
                    <thead>
                      <tr className="bg-gray-900 text-white">
                        <th className="px-4 py-3 text-left text-[11px] font-black uppercase tracking-widest">Member</th>
                        <th className="px-4 py-3 text-left text-[11px] font-black uppercase tracking-widest">Contact</th>
                        <th className="px-4 py-3 text-left text-[11px] font-black uppercase tracking-widest">Service / Amount</th>
                        <th className="px-4 py-3 text-left text-[11px] font-black uppercase tracking-widest">Proforma</th>
                        <th className="px-4 py-3 text-left text-[11px] font-black uppercase tracking-widest">Status</th>
                        <th className="px-4 py-3 text-left text-[11px] font-black uppercase tracking-widest">By / Date</th>
                        <th className="px-4 py-3 text-left text-[11px] font-black uppercase tracking-widest">Action</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100 bg-white">
                      {pagedReqs.map((req, idx) => {
                        const badgeColors = {
                          'Pending': 'bg-amber-100 text-amber-700',
                          'Invoice Ready': 'bg-teal-100 text-teal-700',
                          'Sent to Member': 'bg-indigo-100 text-indigo-700',
                          'Partially Paid': 'bg-orange-100 text-orange-700',
                          'Paid': 'bg-emerald-100 text-emerald-700',
                          'Invoice Created': 'bg-emerald-100 text-emerald-700',
                        };
                        const badgeLabels = {
                          'Pending': '⏳ Awaiting Proforma',
                          'Invoice Ready': '📋 Proforma Sent',
                          'Sent to Member': '📨 Sent to Member',
                          'Partially Paid': '💳 Part Paid',
                          'Paid': '✅ Paid',
                          'Invoice Created': '✅ Invoice Created',
                        };
                        let pd = null;
                        try { pd = req.proforma_data ? (typeof req.proforma_data === 'string' ? JSON.parse(req.proforma_data) : req.proforma_data) : null; } catch (_) {}
                        const phone = String(req.contact_number || '').replace(/\D/g, '');
                        const dialPhone = phone.length === 10 ? `91${phone}` : phone;
                        const proformaWaMsg = pd && dialPhone ? [
                          `Dear ${req.member_name},`,
                          ``,
                          `Please find below your Proforma Invoice for the service charge related to your placement.`,
                          ``,
                          `📋 Proforma No: ${req.proforma_number}`,
                          pd.description ? `📌 ${pd.description}` : null,
                          `✅ Total Payable: ₹${Number(pd.total_amount || req.paid_amount || 0).toLocaleString('en-IN')}`,
                          pd.due_date ? `📅 Due Date: ${pd.due_date}` : null,
                          ``,
                          `Payment: UPI lwin123@fbl  |  A/c: 1256955555 IFSC: FDRL00025 Federal Bank`,
                          ``,
                          `Please share payment screenshot after paying. – Dani Job Club`,
                        ].filter(Boolean).join('\n') : null;
                        const inv = invoices.find((i) => i.id === req.invoice_id);
                        const invTotal = inv ? parseFloat(inv.total_amount || 0).toFixed(2) : Number(req.paid_amount).toFixed(2);
                        const invoiceWaMsg = [
                          `Dear ${req.member_name},`,
                          ``,
                          `Your tax invoice for ${req.payment_type} service has been issued.`,
                          ``,
                          `🧾 Invoice No: ${req.invoice_number}`,
                          `💰 Amount: ₹${invTotal}`,
                          ``,
                          `Thank you for your payment. – Dani Job Club`,
                        ].join('\n');
                        return (
                          <tr key={req.id} className={`transition hover:bg-yellow-50/40 ${idx % 2 !== 0 ? 'bg-gray-50/60' : ''}`}>
                            {/* Member */}
                            <td className="px-4 py-3 whitespace-nowrap">
                              <p className="font-black text-gray-900 text-sm leading-tight">{req.member_name}</p>
                              {req.member_id && <p className="text-xs font-bold text-yellow-600 mt-0.5">ID: {req.member_id}</p>}
                            </td>
                            {/* Contact */}
                            <td className="px-4 py-3 text-xs text-gray-600">
                              {req.place && <p>📍 {[req.place, req.district].filter(Boolean).join(', ')}</p>}
                              {req.contact_number && <p className="mt-0.5">📞 {req.contact_number}</p>}
                            </td>
                            {/* Service / Amount */}
                            <td className="px-4 py-3 whitespace-nowrap">
                              <p className="text-xs font-semibold text-gray-500">{req.payment_type}</p>
                              <p className="text-base font-black text-gray-900 mt-0.5">₹{Number(req.paid_amount).toLocaleString('en-IN')}</p>
                            </td>
                            {/* Proforma */}
                            <td className="px-4 py-3 text-xs">
                              {req.proforma_number ? (
                                <div>
                                  <p className="font-black text-teal-700">{req.proforma_number}</p>
                                  {pd?.description && <p className="text-gray-500 max-w-[160px] truncate mt-0.5">{pd.description}</p>}
                                  {pd && <p className="text-gray-600 mt-0.5">GST {pd.gst_percent || 0}% · ₹{Number(pd.total_amount || req.paid_amount).toLocaleString('en-IN')}</p>}
                                  {pd?.due_date && <p className="text-gray-400 mt-0.5">Due: {fmtDate(pd.due_date)}</p>}
                                </div>
                              ) : <span className="text-gray-300">—</span>}
                            </td>
                            {/* Status */}
                            <td className="px-4 py-3 whitespace-nowrap">
                              <span className={`rounded-full px-2.5 py-1 text-[11px] font-black ${badgeColors[req.status] || 'bg-gray-100 text-gray-500'}`}>
                                {badgeLabels[req.status] || req.status}
                              </span>
                              <InstallmentProgress summary={req.installment_summary} />
                            </td>
                            {/* By / Date */}
                            <td className="px-4 py-3 text-xs text-gray-500 whitespace-nowrap">
                              <p className="font-semibold">{req.hr_name}</p>
                              <p className="mt-0.5">{fmtDate(req.created_at)}</p>
                            </td>
                            {/* Action */}
                            <td className="px-4 py-3 whitespace-nowrap">
                              {req.payment_type === 'Service Charge' && ['Invoice Ready', 'Sent to Member', 'Partially Paid'].includes(req.status) && (
                                <div className="mb-1.5 flex flex-wrap gap-1">
                                  <button type="button" onClick={() => setPlanModal(req)}
                                    className="rounded-xl border border-orange-300 bg-orange-50 px-2.5 py-1 text-[11px] font-black text-orange-700 hover:bg-orange-100">
                                    💳 {req.installment_summary?.plan === 'Installment' ? 'Edit plan' : 'Installments'}
                                  </button>
                                  {req.status === 'Partially Paid' && req.installment_summary?.next_due && (
                                    <button type="button" onClick={() => setRecordModal({ inst: req.installment_summary.next_due, memberName: req.member_name })}
                                      className="rounded-xl bg-emerald-600 px-2.5 py-1 text-[11px] font-black text-white hover:bg-emerald-700">
                                      ✅ Record balance
                                    </button>
                                  )}
                                </div>
                              )}
                              {req.status === 'Pending' && (
                                <button type="button" onClick={() => handleOpenProformaModal(req)}
                                  className="rounded-xl bg-teal-600 px-3 py-1.5 text-xs font-black text-white hover:bg-teal-700 transition">
                                  📋 Create Proforma
                                </button>
                              )}
                              {req.status === 'Invoice Ready' && dialPhone && proformaWaMsg && (
                                <a href={`https://wa.me/${dialPhone}?text=${encodeURIComponent(proformaWaMsg)}`}
                                  target="_blank" rel="noopener noreferrer"
                                  className="rounded-xl border border-green-400 bg-green-50 px-3 py-1.5 text-xs font-black text-green-800 hover:bg-green-100 transition inline-block">
                                  💬 Send to Member
                                </a>
                              )}
                              {req.status === 'Paid' && (
                                <button type="button" onClick={() => handleOpenConvertModal(req)}
                                  className="rounded-xl bg-black px-3 py-1.5 text-xs font-black text-yellow-300 hover:bg-gray-800 transition">
                                  🧾 Convert to Invoice
                                </button>
                              )}
                              {req.status === 'Invoice Created' && req.invoice_number && (
                                <div className="flex flex-col gap-1">
                                  <span className="text-xs font-bold text-emerald-600">#{req.invoice_number}</span>
                                  {dialPhone && (
                                    <a href={`https://wa.me/${dialPhone}?text=${encodeURIComponent(invoiceWaMsg)}`}
                                      target="_blank" rel="noopener noreferrer"
                                      className="rounded-xl border border-green-400 bg-green-50 px-2.5 py-1 text-[11px] font-black text-green-800 hover:bg-green-100 text-center inline-block">
                                      💬 Send Invoice
                                    </a>
                                  )}
                                  {inv && (
                                    <button type="button" onClick={() => printInvoice(inv)}
                                      className="rounded-xl border border-gray-300 bg-white px-2.5 py-1 text-[11px] font-bold text-gray-700 hover:bg-gray-50 text-center">
                                      🖨 Print
                                    </button>
                                  )}
                                </div>
                              )}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
                {/* Requests pagination */}
                {totalReqPages > 1 && (
                  <div className="flex items-center justify-between px-1 py-2">
                    <p className="text-xs text-gray-500">Page {safeReqPage} of {totalReqPages} · {paymentRequests.length} requests</p>
                    <div className="flex gap-2">
                      <button type="button" onClick={() => setReqPage(p => Math.max(1, p - 1))} disabled={safeReqPage <= 1}
                        className="rounded-lg border border-gray-200 px-3 py-1.5 text-xs font-bold text-gray-600 hover:bg-gray-50 disabled:opacity-40">← Prev</button>
                      <button type="button" onClick={() => setReqPage(p => Math.min(totalReqPages, p + 1))} disabled={safeReqPage >= totalReqPages}
                        className="rounded-lg border border-gray-200 px-3 py-1.5 text-xs font-bold text-gray-600 hover:bg-gray-50 disabled:opacity-40">Next →</button>
                    </div>
                  </div>
                )}
                </div>
              )}
            </div>
          )}

          {/* ══════════════════════════════════════════════════════════════
              CONVERT PROFORMA → FINAL INVOICE MODAL
          ══════════════════════════════════════════════════════════════ */}
          {convertModal && (
            <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 px-4">
              <div className="relative w-full max-w-lg rounded-3xl bg-white shadow-2xl overflow-hidden">
                {/* Modal header */}
                <div className="bg-gradient-to-r from-black to-gray-800 px-6 py-4 flex items-center justify-between">
                  <div>
                    <p className="text-base font-black text-yellow-300">🧾 Convert Proforma → Final Invoice</p>
                    <p className="text-xs text-gray-300 mt-0.5">{convertModal.member_name}</p>
                  </div>
                  <button
                    type="button"
                    onClick={() => { setConvertModal(null); setConvertFeedback(''); setConvertedInvoice(null); }}
                    className="rounded-full p-1.5 hover:bg-white/10 text-white"
                  >
                    ✕
                  </button>
                </div>

                <div className="px-6 py-5 space-y-4">
                  {/* Proforma summary */}
                  {(() => {
                    let pd = null;
                    try { pd = convertModal.proforma_data ? (typeof convertModal.proforma_data === 'string' ? JSON.parse(convertModal.proforma_data) : convertModal.proforma_data) : null; } catch (_) {}
                    return pd ? (
                      <div className="rounded-xl bg-yellow-50 border border-yellow-200 px-4 py-3 text-sm space-y-1">
                        <p className="text-xs font-black text-yellow-700 uppercase tracking-wider mb-2">Proforma Details (will be used for invoice)</p>
                        <p className="font-semibold text-gray-800">{pd.description}</p>
                        <div className="flex flex-wrap gap-4 text-xs mt-2">
                          <span><span className="font-bold text-gray-500">Proforma No:</span> {convertModal.proforma_number}</span>
                          <span><span className="font-bold text-gray-500">Amount:</span> ₹{Number(pd.amount || 0).toLocaleString('en-IN')}</span>
                          {pd.gst_percent > 0 && <span><span className="font-bold text-gray-500">GST {pd.gst_percent}%:</span> ₹{Number(pd.gst_amount || 0).toFixed(2)}</span>}
                          <span className="font-black text-gray-900"><span className="font-bold text-gray-500">Total:</span> ₹{Number(pd.total_amount || 0).toLocaleString('en-IN')}</span>
                        </div>
                      </div>
                    ) : (
                      <div className="rounded-xl bg-gray-50 border border-gray-200 px-4 py-3 text-sm text-gray-600">
                        <p><span className="font-bold">Amount:</span> ₹{Number(convertModal.paid_amount || 0).toLocaleString('en-IN')}</p>
                      </div>
                    );
                  })()}

                  {/* Invoice date fields */}
                  {!convertedInvoice && (
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <label className="block text-xs font-black text-gray-700 mb-1">Invoice Date <span className="text-red-500">*</span></label>
                        <input
                          type="date"
                          value={convertForm.invoice_date}
                          onChange={(e) => setConvertForm((f) => ({ ...f, invoice_date: e.target.value }))}
                          className="w-full rounded-xl border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-yellow-400"
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-black text-gray-700 mb-1">Due Date</label>
                        <input
                          type="date"
                          value={convertForm.due_date}
                          onChange={(e) => setConvertForm((f) => ({ ...f, due_date: e.target.value }))}
                          className="w-full rounded-xl border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-yellow-400"
                        />
                      </div>
                      <div className="col-span-2">
                        <label className="block text-xs font-black text-gray-700 mb-1">Notes (optional)</label>
                        <textarea
                          rows={2}
                          value={convertForm.notes}
                          onChange={(e) => setConvertForm((f) => ({ ...f, notes: e.target.value }))}
                          placeholder="Any additional notes for the invoice…"
                          className="w-full rounded-xl border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-yellow-400 resize-none"
                        />
                      </div>
                    </div>
                  )}

                  {convertFeedback && (
                    <p className={`text-sm font-semibold rounded-xl px-3 py-2 ${convertFeedback.startsWith('✅') ? 'bg-emerald-50 text-emerald-700' : 'bg-red-50 text-red-600'}`}>
                      {convertFeedback}
                    </p>
                  )}

                  {/* After conversion: show invoice + WhatsApp send button */}
                  {convertedInvoice && (
                    <div className="rounded-xl border-2 border-emerald-300 bg-emerald-50 px-4 py-4 space-y-3">
                      <p className="text-sm font-extrabold text-emerald-900">Invoice Created Successfully!</p>
                      <div className="text-xs space-y-1">
                        <p><span className="font-bold text-gray-500">Invoice No:</span> <span className="font-black text-emerald-800 font-mono">{convertedInvoice.invoice_number}</span></p>
                        <p><span className="font-bold text-gray-500">Amount:</span> <span className="font-black">{fmt(convertedInvoice.total_amount)}</span></p>
                      </div>
                      {/* Send invoice to member via WhatsApp */}
                      {(() => {
                        const phone = String(convertModal.contact_number || '').replace(/\D/g, '');
                        const dialPhone = phone.length === 10 ? `91${phone}` : phone;
                        const waMsg = [
                          `Dear ${convertModal.member_name},`,
                          ``,
                          `Your tax invoice for the service charge has been issued.`,
                          ``,
                          `🧾 Invoice No: ${convertedInvoice.invoice_number}`,
                          `📅 Date: ${fmtDate(convertedInvoice.invoice_date)}`,
                          `💰 Total Amount: ₹${parseFloat(convertedInvoice.total_amount || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}`,
                          ``,
                          `Thank you for your payment. We wish you the very best in your new career! – Dani Job Club`,
                        ].join('\n');
                        return dialPhone ? (
                          <a
                            href={`https://wa.me/${dialPhone}?text=${encodeURIComponent(waMsg)}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="flex items-center justify-center gap-2 w-full rounded-xl bg-green-600 px-4 py-2.5 text-sm font-extrabold text-white hover:bg-green-700"
                          >
                            💬 Send Invoice to Member via WhatsApp
                          </a>
                        ) : null;
                      })()}
                      <button
                        type="button"
                        onClick={() => printInvoice(convertedInvoice)}
                        className="w-full rounded-xl border border-gray-300 bg-white px-4 py-2 text-sm font-bold text-gray-700 hover:bg-gray-50"
                      >
                        🖨️ Print / Download Invoice
                      </button>
                    </div>
                  )}

                  {!convertedInvoice && (
                    <button
                      type="button"
                      onClick={handleConvertToInvoice}
                      disabled={convertSaving || !convertForm.invoice_date}
                      className="w-full rounded-2xl bg-black py-3 text-sm font-black text-yellow-300 hover:bg-gray-900 disabled:opacity-50 transition"
                    >
                      {convertSaving ? '⏳ Creating Invoice…' : '🧾 Create Final Tax Invoice'}
                    </button>
                  )}

                  {convertedInvoice && (
                    <button
                      type="button"
                      onClick={() => { setConvertModal(null); setConvertFeedback(''); setConvertedInvoice(null); }}
                      className="w-full rounded-2xl border border-gray-200 bg-white py-2.5 text-sm font-bold text-gray-600 hover:bg-gray-50"
                    >
                      Close
                    </button>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* ══════════════════════════════════════════════════════════════
              PROFORMA MODAL (Accountant → create proforma → send to member)
          ══════════════════════════════════════════════════════════════ */}
          {planModal && (
            <PaymentPlanModal req={planModal} API_URL={API_URL} onClose={() => setPlanModal(null)}
              onSaved={() => { setPlanModal(null); fetchPaymentRequests(); setDuesRefreshKey((k) => k + 1); }} />
          )}
          {recordModal && (
            <RecordInstallmentModal inst={recordModal.inst} memberName={recordModal.memberName} API_URL={API_URL} onClose={() => setRecordModal(null)}
              onSaved={() => { setRecordModal(null); fetchPaymentRequests(); setDuesRefreshKey((k) => k + 1); }} />
          )}
          {proformaModal && (
            <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 px-4">
              <div className="relative w-full max-w-md rounded-3xl bg-white shadow-2xl overflow-hidden">
                {/* Modal header */}
                <div className={`px-6 py-4 flex items-center justify-between ${proformaSentResult ? 'bg-emerald-600' : 'bg-teal-600'}`}>
                  <div>
                    <p className="text-base font-black text-white">
                      {proformaSentResult ? '✅ Proforma Created' : '📋 Create Proforma Invoice'}
                    </p>
                    <p className="text-xs text-teal-100 mt-0.5">{proformaModal.member_name}</p>
                  </div>
                  <button
                    type="button"
                    onClick={() => { setProformaModal(null); setProformaFeedback(''); setProformaSentResult(null); }}
                    className="rounded-full p-1.5 hover:bg-teal-700 text-white"
                  >
                    ✕
                  </button>
                </div>

                <div className="px-6 py-5 space-y-4">
                  {/* ── SUCCESS STATE: show proforma summary + send to member ── */}
                  {proformaSentResult ? (() => {
                    const pd = (() => { try { return proformaSentResult.proforma_data ? (typeof proformaSentResult.proforma_data === 'string' ? JSON.parse(proformaSentResult.proforma_data) : proformaSentResult.proforma_data) : null; } catch (_) { return null; } })();
                    const phone = String(proformaModal.contact_number || '').replace(/\D/g, '');
                    const dialPhone = phone.length === 10 ? `91${phone}` : phone;
                    const waMsg = [
                      `Dear ${proformaModal.member_name},`,
                      ``,
                      `Please find below your Proforma Invoice for the service charge related to your placement.`,
                      ``,
                      `📋 Proforma No: ${proformaSentResult.proforma_number}`,
                      pd?.description ? `📌 ${pd.description}` : null,
                      `✅ Total Payable: ₹${Number(pd?.total_amount || proformaModal.paid_amount || 0).toLocaleString('en-IN')}`,
                      proformaForm.payment_plan === 'Installment' ? `💳 Payable in 2 installments: ${rupees(proformaForm.first_amount)} now, balance ${rupees(Number(pd?.total_amount || 0) - Number(proformaForm.first_amount))} by ${fmtDate(proformaForm.second_due_date)}` : null,
                      pd?.due_date ? `📅 Due Date: ${pd.due_date}` : null,
                      ``,
                      `Payment: UPI lwin123@fbl  |  A/c: 1256955555 IFSC: FDRL00025 Federal Bank`,
                      ``,
                      `Please share payment screenshot after paying. – Dani Job Club`,
                    ].filter(Boolean).join('\n');
                    return (
                      <div className="space-y-4">
                        {/* Proforma summary */}
                        <div className="rounded-xl bg-emerald-50 border border-emerald-200 px-4 py-3 space-y-1 text-sm">
                          <p className="font-black text-emerald-800 text-base">Proforma No: {proformaSentResult.proforma_number}</p>
                          {pd?.description && <p className="text-gray-600 text-xs">{pd.description}</p>}
                          <div className="flex flex-wrap gap-4 mt-1 text-xs">
                            {pd?.gst_percent > 0 && <span><span className="text-gray-500">GST {pd.gst_percent}%:</span> ₹{Number(pd.gst_amount || 0).toFixed(2)}</span>}
                            <span className="font-black text-emerald-900 text-base">Total: ₹{Number(pd?.total_amount || proformaModal.paid_amount || 0).toLocaleString('en-IN')}</span>
                            {pd?.due_date && <span><span className="text-gray-500">Due:</span> {pd.due_date}</span>}
                          </div>
                        </div>

                        {/* Send directly to member */}
                        {dialPhone ? (
                          <a
                            href={`https://wa.me/${dialPhone}?text=${encodeURIComponent(waMsg)}`}
                            target="_blank" rel="noopener noreferrer"
                            className="flex items-center justify-center gap-2 w-full rounded-2xl bg-green-600 py-3 text-sm font-black text-white hover:bg-green-700"
                          >
                            💬 Send Proforma to Member via WhatsApp
                          </a>
                        ) : (
                          <p className="text-xs text-amber-600 font-semibold text-center">⚠️ No phone number on file for this member</p>
                        )}

                        <p className="text-xs text-gray-400 text-center">HR Assistant has also been notified to follow up on payment.</p>

                        <button
                          type="button"
                          onClick={() => { setProformaModal(null); setProformaFeedback(''); setProformaSentResult(null); }}
                          className="w-full rounded-2xl border border-gray-200 bg-white py-2.5 text-sm font-bold text-gray-600 hover:bg-gray-50"
                        >
                          Close
                        </button>
                      </div>
                    );
                  })() : (
                    <>
                      {/* ── FORM STATE ── */}
                      {/* Description */}
                      <div>
                        <label className="block text-xs font-black text-gray-700 mb-1">Description</label>
                        <textarea
                          rows={2}
                          value={proformaForm.description}
                          onChange={(e) => setProformaForm((f) => ({ ...f, description: e.target.value }))}
                          className="w-full rounded-xl border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-teal-400"
                        />
                      </div>

                      {/* Amount + GST */}
                      <div className="grid grid-cols-2 gap-3">
                        <div>
                          <label className="block text-xs font-black text-gray-700 mb-1">Amount (₹)</label>
                          <input
                            type="number"
                            min="0"
                            value={proformaForm.amount}
                            onChange={(e) => setProformaForm((f) => ({ ...f, amount: e.target.value }))}
                            className="w-full rounded-xl border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-teal-400"
                          />
                        </div>
                        <div>
                          <label className="block text-xs font-black text-gray-700 mb-1">GST %</label>
                          <select
                            value={proformaForm.gst_percent}
                            onChange={(e) => setProformaForm((f) => ({ ...f, gst_percent: e.target.value }))}
                            className="w-full rounded-xl border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-teal-400"
                          >
                            <option value="0">0%</option>
                            <option value="5">5%</option>
                            <option value="12">12%</option>
                            <option value="18">18%</option>
                          </select>
                        </div>
                      </div>

                      {/* Total preview */}
                      {proformaForm.amount && (
                        <div className="rounded-xl bg-teal-50 border border-teal-200 px-4 py-2 text-sm">
                          <span className="text-gray-600">Total (incl. GST): </span>
                          <span className="font-black text-teal-800">
                            ₹{(Number(proformaForm.amount) * (1 + Number(proformaForm.gst_percent) / 100)).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                          </span>
                        </div>
                      )}

                      {/* Due date */}
                      <div>
                        <label className="block text-xs font-black text-gray-700 mb-1">Due Date</label>
                        <input
                          type="date"
                          value={proformaForm.due_date}
                          onChange={(e) => setProformaForm((f) => ({ ...f, due_date: e.target.value }))}
                          className="w-full rounded-xl border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-teal-400"
                        />
                      </div>

                      {/* Full or 2 installments — Accounts decides */}
                      {Number(proformaForm.amount) > 0 && (
                        <PaymentPlanFields
                          total={Number(proformaForm.amount) * (1 + Number(proformaForm.gst_percent) / 100)}
                          plan={proformaForm}
                          onChange={(next) => setProformaForm((f) => ({ ...f, ...next }))}
                        />
                      )}

                      {proformaFeedback && (
                        <p className={`text-sm font-semibold rounded-xl px-3 py-2 ${proformaFeedback.startsWith('✅') ? 'bg-emerald-50 text-emerald-700' : 'bg-red-50 text-red-600'}`}>
                          {proformaFeedback}
                        </p>
                      )}

                      <button
                        type="button"
                        onClick={handleSendProforma}
                        disabled={proformaSaving || !proformaForm.amount}
                        className="w-full rounded-2xl bg-teal-600 py-3 text-sm font-black text-white hover:bg-teal-700 disabled:opacity-50 transition"
                      >
                        {proformaSaving ? 'Creating…' : '📋 Create Proforma Invoice'}
                      </button>
                    </>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* ══════════════════════════════════════════════════════════════
              TAB: STATISTICS
          ══════════════════════════════════════════════════════════════ */}
          {tab === 'stats' && (
            <div className="space-y-6">
              {/* KPI Cards */}
              <div className="grid grid-cols-2 gap-4 md:grid-cols-4">
                {[
                  { label: 'Total Invoices', val: stats.total, icon: '🧾', border: 'border-yellow-200', bg: 'bg-yellow-50', text: 'text-yellow-700', num: 'text-yellow-900' },
                  { label: 'Paid', val: stats.paid, icon: '✅', border: 'border-emerald-200', bg: 'bg-emerald-50', text: 'text-emerald-600', num: 'text-emerald-900' },
                  { label: 'Unpaid', val: stats.unpaid, icon: '⏳', border: 'border-red-200', bg: 'bg-red-50', text: 'text-red-600', num: 'text-red-900' },
                  { label: 'Overdue', val: stats.overdue, icon: '⚠️', border: 'border-orange-200', bg: 'bg-orange-50', text: 'text-orange-600', num: 'text-orange-900' },
                  { label: 'Cancelled', val: stats.cancelled, icon: '🚫', border: 'border-gray-200', bg: 'bg-gray-50', text: 'text-gray-500', num: 'text-gray-700' },
                  { label: 'Revenue Collected', val: fmt(stats.revenue), icon: '💰', border: 'border-emerald-300', bg: 'bg-emerald-50', text: 'text-emerald-700', num: 'text-emerald-900' },
                  { label: 'Outstanding', val: fmt(stats.outstanding), icon: '📤', border: 'border-yellow-300', bg: 'bg-yellow-50', text: 'text-yellow-700', num: 'text-yellow-900' },
                ].map((s) => (
                  <div key={s.label} className={`rounded-[20px] border ${s.border} ${s.bg} p-5 flex items-start gap-3`}>
                    <span className="text-2xl mt-0.5">{s.icon}</span>
                    <div>
                      <p className={`text-2xl font-black ${s.num}`}>{s.val}</p>
                      <p className={`text-xs font-semibold uppercase tracking-wider mt-1 ${s.text}`}>{s.label}</p>
                    </div>
                  </div>
                ))}
              </div>

              {/* Paid Invoices */}
              <div className="rounded-[24px] border border-yellow-200 bg-white p-5 shadow-sm">
                <p className="text-xs font-black uppercase tracking-[0.2em] text-yellow-700 mb-4">Paid Invoices</p>
                {pagedPaid.length === 0 ? (
                  <div className="py-10 text-center">
                    <p className="text-3xl mb-2">💰</p>
                    <p className="text-gray-400 text-sm font-semibold">No paid invoices yet.</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                  <div className="rounded-2xl overflow-hidden border border-gray-100">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="bg-yellow-50 border-b border-yellow-100">
                          <th className="px-4 py-2.5 text-left text-xs font-black text-gray-500 uppercase tracking-wider">Invoice</th>
                          <th className="px-4 py-2.5 text-left text-xs font-black text-gray-500 uppercase tracking-wider">Client</th>
                          <th className="px-4 py-2.5 text-right text-xs font-black text-gray-500 uppercase tracking-wider">Total</th>
                          <th className="px-4 py-2.5 text-left text-xs font-black text-gray-500 uppercase tracking-wider">Date</th>
                        </tr>
                      </thead>
                      <tbody>
                        {pagedPaid.map((inv, idx) => (
                          <tr key={inv.id} className={`border-b border-gray-50 hover:bg-yellow-50/50 transition-colors ${idx % 2 === 0 ? 'bg-white' : 'bg-gray-50/30'}`}>
                            <td className="px-4 py-3 font-mono text-yellow-700 text-xs font-bold">{inv.invoice_number}</td>
                            <td className="px-4 py-3 font-black text-gray-900">{inv.client_name}</td>
                            <td className="px-4 py-3 text-emerald-700 font-black text-right">{fmt(inv.total_amount)}</td>
                            <td className="px-4 py-3 text-gray-400 text-xs">{fmtDate(inv.invoice_date)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  {/* Stats pagination */}
                  {totalStatsPages > 1 && (
                    <div className="flex items-center justify-between px-1 py-2">
                      <p className="text-xs text-gray-500">Page {safeStatsPage} of {totalStatsPages} · {paidInvoices.length} paid invoices</p>
                      <div className="flex gap-2">
                        <button type="button" onClick={() => setStatsPage(p => Math.max(1, p - 1))} disabled={safeStatsPage <= 1}
                          className="rounded-lg border border-gray-200 px-3 py-1.5 text-xs font-bold text-gray-600 hover:bg-gray-50 disabled:opacity-40">← Prev</button>
                        <button type="button" onClick={() => setStatsPage(p => Math.min(totalStatsPages, p + 1))} disabled={safeStatsPage >= totalStatsPages}
                          className="rounded-lg border border-gray-200 px-3 py-1.5 text-xs font-bold text-gray-600 hover:bg-gray-50 disabled:opacity-40">Next →</button>
                      </div>
                    </div>
                  )}
                  </div>
                )}
              </div>
            </div>
          )}

          {/* ══════════════════════════════════════════════════════════════
              TAB: PARTNER COMMISSIONS
          ══════════════════════════════════════════════════════════════ */}
          {tab === 'partner-commissions' && (
            <div className="space-y-4">
              <div className="flex items-center justify-between flex-wrap gap-2">
                <div className="flex items-center gap-3 flex-wrap">
                  <h3 className="text-xl font-black text-black">Partner Commissions</h3>
                  <p className="w-full text-sm text-gray-500">Same rate for every placement: members referred by partners and vacancies shared by partners.</p>
                </div>
              </div>
              {(() => {
                const money = (list) => list.reduce((sum, c) => sum + (parseFloat(c.commission_amount || '0') || 0), 0);
                const rupees = (n) => `₹${n.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
                const pending = partnerCommissions.filter((c) => c.status !== 'Paid');
                const paid = partnerCommissions.filter((c) => c.status === 'Paid');
                const awaiting = pending.filter((c) => !(parseFloat(c.commission_amount || '0') > 0));
                return (
                  <div className="grid grid-cols-2 gap-3 lg:grid-cols-5">
                    {[
                      { label: 'To Pay Now', value: rupees(money(pending)), sub: `${pending.length - awaiting.length} ready to pay`, cls: 'bg-purple-600 text-white', subCls: 'text-purple-100' },
                      { label: 'Awaiting Amount', value: awaiting.length, sub: 'service charge not recorded', cls: 'bg-orange-50 text-orange-800 border border-orange-200', subCls: 'text-orange-600' },
                      { label: 'Paid', value: rupees(money(paid)), sub: `${paid.length} payments`, cls: 'bg-emerald-600 text-white', subCls: 'text-emerald-100' },
                      { label: 'Member Referrals', value: rupees(money(partnerCommissions.filter((c) => c.source_type !== 'vacancy'))), sub: 'all time', cls: 'bg-white border border-gray-200 text-black', subCls: 'text-gray-500' },
                      { label: 'Shared Vacancies', value: rupees(money(partnerCommissions.filter((c) => c.source_type === 'vacancy'))), sub: 'all time', cls: 'bg-white border border-gray-200 text-black', subCls: 'text-gray-500' }
                    ].map((card) => (
                      <div key={card.label} className={`rounded-2xl px-4 py-3 shadow-sm ${card.cls}`}>
                        <p className={`text-[10px] font-black uppercase tracking-[0.16em] ${card.subCls}`}>{card.label}</p>
                        <p className="mt-1 text-xl font-black">{card.value}</p>
                        <p className={`text-[11px] font-bold ${card.subCls}`}>{card.sub}</p>
                      </div>
                    ))}
                  </div>
                );
              })()}
              <div className="grid grid-cols-1 gap-2 rounded-2xl border border-purple-100 bg-purple-50/50 p-3 sm:grid-cols-3">
                <input value={pcFilter.search} onChange={(e) => setPcFilter((p) => ({ ...p, search: e.target.value }))} placeholder="🔍 Partner, candidate, company" className="rounded-xl border border-gray-300 bg-white px-3 py-2 text-sm" />
                <select value={pcFilter.status} onChange={(e) => setPcFilter((p) => ({ ...p, status: e.target.value }))} className="rounded-xl border border-gray-300 bg-white px-3 py-2 text-sm">
                  <option value="Pending">Pending payment</option>
                  <option value="Paid">Paid</option>
                  <option value="">All statuses</option>
                </select>
                <select value={pcFilter.source} onChange={(e) => setPcFilter((p) => ({ ...p, source: e.target.value }))} className="rounded-xl border border-gray-300 bg-white px-3 py-2 text-sm">
                  <option value="">All sources</option>
                  <option value="member">👤 Member referrals</option>
                  <option value="vacancy">🏢 Shared vacancies</option>
                </select>
              </div>
              {pcLoading && <p className="text-sm text-gray-500">Loading…</p>}
              {!pcLoading && partnerCommissions.length === 0 && (
                <div className="rounded-2xl border border-dashed border-gray-300 bg-gray-50 px-4 py-10 text-center text-sm text-gray-500">
                  No partner commissions yet.
                </div>
              )}
              {!pcLoading && partnerCommissions.length > 0 && (
                <div className="overflow-x-auto rounded-2xl border border-gray-200">
                  <table className="w-full text-sm min-w-[780px]">
                    <thead>
                      <tr className="bg-purple-50 border-b border-purple-100">
                        {['Date','Partner','Member Name','Job Title','Service Charge','Commission %','Commission Amount','Status','Action'].map((h) => (
                          <th key={h} className="px-4 py-3 text-left text-xs font-black text-gray-600 uppercase tracking-wider whitespace-nowrap">{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {partnerCommissions.filter((c) => {
                        const q = pcFilter.search.trim().toLowerCase();
                        if (pcFilter.status && (pcFilter.status === 'Paid') !== (c.status === 'Paid')) return false;
                        if (pcFilter.source && (pcFilter.source === 'vacancy') !== (c.source_type === 'vacancy')) return false;
                        return !q || [c.partner_org_name, c.partner_user_id, c.member_name, c.company, c.job_title].some((v) => String(v || '').toLowerCase().includes(q));
                      }).map((c, idx) => (
                        <tr key={c.id} className={`border-b border-gray-100 hover:bg-purple-50/30 ${idx % 2 === 0 ? 'bg-white' : 'bg-gray-50/30'}`}>
                          <td className="px-4 py-3 text-xs text-gray-500 whitespace-nowrap">{c.created_at ? c.created_at.slice(0, 10) : '—'}</td>
                          <td className="px-4 py-3 font-semibold text-gray-800">{c.partner_org_name || c.partner_user_id || '—'}</td>
                          <td className="px-4 py-3 font-semibold text-gray-900">
                          {c.member_name || '—'}
                          <p className={`mt-0.5 text-[10px] font-bold ${c.source_type === 'vacancy' ? 'text-blue-600' : 'text-gray-400'}`}>
                            {c.source_type === 'vacancy' ? '🏢 Partner shared vacancy' : '👤 Partner member referral'}
                          </p>
                          </td>
                          <td className="px-4 py-3 text-gray-700">{c.job_title || '—'}</td>
                          <td className="px-4 py-3 text-gray-700 whitespace-nowrap">₹{c.service_charge_amount || '0'}</td>
                          <td className="px-4 py-3 text-gray-700 whitespace-nowrap">{c.commission_percentage || '0'}%</td>
                          <td className="px-4 py-3 font-black text-purple-700 whitespace-nowrap">₹{c.commission_amount || '0'}</td>
                          <td className="px-4 py-3">
                            {c.status === 'Paid' ? (
                              <span className="inline-flex items-center gap-1 rounded-full bg-emerald-100 border border-emerald-300 px-2.5 py-1 text-xs font-black text-emerald-700">
                                ✓ Paid {c.paid_at ? c.paid_at.slice(0, 10) : ''}
                              </span>
                            ) : (
                              <span className="rounded-full bg-yellow-100 border border-yellow-300 px-2.5 py-1 text-xs font-black text-yellow-700">Pending</span>
                            )}
                          </td>
                          <td className="px-4 py-3 whitespace-nowrap">
                            {c.status !== 'Paid' && !(parseFloat(c.commission_amount || '0') > 0) ? (
                              <span className="text-xs font-bold text-orange-600" title="The service charge for this placement has not been recorded yet">Awaiting service charge</span>
                            ) : c.status !== 'Paid' ? (
                              <button
                                type="button"
                                onClick={() => { setPcPayModal(c); setPcScreenshot(null); setPcPayFeedback(''); }}
                                className="rounded-xl bg-purple-600 px-3 py-1.5 text-xs font-black text-white hover:bg-purple-700"
                              >
                                Mark Paid + Receipt
                              </button>
                            ) : c.transaction_screenshot ? (
                              <button
                                type="button"
                                onClick={() => openCommissionReceipt(c.id, localStorage.getItem('token'))}
                                className="rounded-xl bg-gray-100 border border-gray-300 px-3 py-1.5 text-xs font-bold text-gray-700 hover:bg-gray-200 inline-block"
                              >
                                🧾 View Receipt
                              </button>
                            ) : (
                              <span className="text-xs text-gray-400">No receipt</span>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}

        </div>
      </div>

      {/* ── PARTNER COMMISSION PAY MODAL ──────────────────────────────────── */}
      {pcPayModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="w-full max-w-sm rounded-[28px] bg-white border border-purple-200 p-6 shadow-2xl">
            <p className="text-xs font-black uppercase tracking-[0.22em] text-purple-700">Mark Commission Paid</p>
            <h3 className="mt-1 text-lg font-black text-gray-900">{pcPayModal.member_name || 'Member'}</h3>
            <p className="text-sm text-gray-600 mt-0.5">Partner: <span className="font-bold">{pcPayModal.partner_org_name || pcPayModal.partner_user_id}</span></p>
            <div className="mt-3 rounded-2xl bg-purple-50 border border-purple-100 px-4 py-3 flex items-center justify-between">
              <span className="text-xs font-semibold text-purple-700">Commission Due</span>
              <span className="text-2xl font-black text-purple-800">₹{pcPayModal.commission_amount}</span>
            </div>
            <p className="mt-1 text-xs text-gray-400 text-center">{pcPayModal.commission_percentage}% of ₹{pcPayModal.service_charge_amount} service charge</p>

            <label className="mt-4 block">
              <span className="text-xs font-black uppercase tracking-wider text-gray-700">Transaction Screenshot</span>
              <input
                type="file"
                accept="image/*,.pdf"
                onChange={(e) => setPcScreenshot(e.target.files?.[0] || null)}
                className="mt-1.5 block w-full text-sm text-gray-600 file:mr-3 file:rounded-xl file:border-0 file:bg-purple-100 file:px-3 file:py-1.5 file:text-xs file:font-bold file:text-purple-700 hover:file:bg-purple-200"
              />
              {pcScreenshot && <p className="mt-1 text-xs text-emerald-600 font-semibold">Selected: {pcScreenshot.name}</p>}
            </label>

            {pcPayFeedback && <p className="mt-2 text-xs font-semibold text-red-600">{pcPayFeedback}</p>}

            <div className="mt-5 flex gap-3">
              <button
                type="button"
                disabled={pcPaying}
                onClick={handleMarkPcPaid}
                className="flex-1 rounded-full bg-purple-700 px-4 py-2.5 font-black text-white text-sm hover:bg-purple-600 disabled:opacity-60"
              >
                {pcPaying ? 'Processing…' : 'Confirm Paid'}
              </button>
              <button
                type="button"
                onClick={() => { setPcPayModal(null); setPcScreenshot(null); setPcPayFeedback(''); }}
                className="rounded-full border border-gray-300 px-4 py-2.5 text-sm font-bold text-gray-600 hover:bg-gray-50"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── CANCEL MODAL ──────────────────────────────────────────────────── */}
      {cancelModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="w-full max-w-sm rounded-[28px] bg-white border border-red-200 p-6 shadow-2xl">
            <p className="text-xs font-black uppercase tracking-[0.22em] text-red-600">Cancel Invoice</p>
            <h3 className="mt-1 text-lg font-black text-gray-900">{cancelModal.invoice_number}</h3>
            <p className="text-sm text-gray-500 mt-0.5">{cancelModal.client_name} — {fmt(cancelModal.total_amount)}</p>
            <label className="mt-4 block text-xs font-semibold text-gray-600 uppercase tracking-wider">
              Cancellation Reason
              <textarea
                value={cancelReason}
                onChange={(e) => setCancelReason(e.target.value)}
                rows={3}
                placeholder="Enter reason…"
                className="mt-1 w-full rounded-xl border border-gray-300 bg-gray-50 px-3 py-2.5 text-sm font-normal focus:outline-none focus:ring-2 focus:ring-red-400 resize-none"
              />
            </label>
            <div className="mt-4 flex gap-3">
              <button
                type="button"
                disabled={cancelling}
                onClick={handleCancel}
                className="flex-1 rounded-full bg-red-600 px-4 py-2.5 font-black text-white text-sm hover:bg-red-500 disabled:opacity-60"
              >
                {cancelling ? 'Cancelling…' : 'Yes, Cancel Invoice'}
              </button>
              <button
                type="button"
                onClick={() => setCancelModal(null)}
                className="flex-1 rounded-full border border-gray-300 bg-white px-4 py-2.5 font-bold text-gray-700 text-sm hover:bg-gray-50"
              >
                Go Back
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── VIEW MODAL ────────────────────────────────────────────────────── */}
      {viewModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="max-h-[90vh] w-full max-w-lg overflow-y-auto rounded-[28px] bg-white border border-yellow-200 shadow-2xl">
            <div className="sticky top-0 bg-[radial-gradient(circle_at_top_left,rgba(250,204,21,0.26),transparent_24%),linear-gradient(135deg,#090909_0%,#1c1c1c_55%,#5b4703_100%)] px-6 py-4 flex justify-between items-start rounded-t-[28px]">
              <div>
                <p className="text-[10px] font-black uppercase tracking-[0.22em] text-yellow-400">Invoice Detail</p>
                <h3 className="text-lg font-black text-white mt-0.5 font-mono">{viewModal.invoice_number}</h3>
              </div>
              <button type="button" onClick={() => setViewModal(null)} className="text-gray-400 hover:text-white text-xl font-bold mt-0.5">✕</button>
            </div>

            <div className="p-6 space-y-4">
              <span className={`inline-block rounded-full border px-3 py-1 text-xs font-black ${STATUS_COLORS[viewModal.payment_status] || 'bg-gray-100 text-gray-600 border-gray-300'} ${viewModal.cancelled ? 'opacity-50 line-through' : ''}`}>
                {viewModal.cancelled ? 'CANCELLED' : viewModal.payment_status}
              </span>

              <div className="rounded-[20px] border border-gray-200 bg-gray-50 p-4">
                <p className="text-[10px] font-black text-gray-400 uppercase tracking-wider mb-2">Client</p>
                <p className="font-black text-gray-900 text-base">{viewModal.client_name}</p>
                {viewModal.client_contact && <p className="text-xs text-gray-500 mt-1">📞 {viewModal.client_contact}</p>}
                {viewModal.client_email && <p className="text-xs text-gray-500">✉ {viewModal.client_email}</p>}
                {viewModal.client_address && <p className="text-xs text-gray-500">📍 {viewModal.client_address}</p>}
              </div>

              <div className="rounded-[20px] border border-gray-200 bg-gray-50 p-4">
                <p className="text-[10px] font-black text-gray-400 uppercase tracking-wider mb-2">Service</p>
                <p className="text-sm text-gray-700 leading-relaxed">{viewModal.service_description}</p>
              </div>

              <div className="rounded-[20px] border-2 border-yellow-300 bg-yellow-50 p-4 space-y-2">
                <div className="flex justify-between text-sm"><span className="text-gray-500 font-semibold">Sub Total</span><span className="font-black text-gray-900">{fmt(viewModal.amount)}</span></div>
                {viewModal.gst_percent > 0 && (
                  <div className="flex justify-between text-sm"><span className="text-gray-500 font-semibold">GST ({viewModal.gst_percent}%)</span><span className="font-black text-gray-900">{fmt(viewModal.gst_amount)}</span></div>
                )}
                <div className="flex justify-between border-t border-yellow-200 pt-2">
                  <span className="font-black text-yellow-800 text-base">Total</span>
                  <span className="font-black text-yellow-900 text-xl">{fmt(viewModal.total_amount)}</span>
                </div>
              </div>

              <div className="flex gap-3">
                <div className="flex-1 rounded-xl border border-gray-200 bg-gray-50 px-4 py-3">
                  <p className="text-[10px] font-black text-gray-400 uppercase tracking-wider">Invoice Date</p>
                  <p className="font-black text-gray-900 mt-0.5">{fmtDate(viewModal.invoice_date)}</p>
                </div>
                {viewModal.due_date && (
                  <div className="flex-1 rounded-xl border border-gray-200 bg-gray-50 px-4 py-3">
                    <p className="text-[10px] font-black text-gray-400 uppercase tracking-wider">Due Date</p>
                    <p className="font-black text-gray-900 mt-0.5">{fmtDate(viewModal.due_date)}</p>
                  </div>
                )}
              </div>

              {viewModal.notes && (
                <div className="rounded-[20px] border border-yellow-200 bg-yellow-50 p-4 text-sm text-gray-700">
                  <span className="font-black text-yellow-800">Notes: </span>{viewModal.notes}
                </div>
              )}

              {viewModal.cancelled && viewModal.cancellation_reason && (
                <div className="rounded-[20px] border border-red-200 bg-red-50 p-4 text-sm text-red-700">
                  <span className="font-black">Cancellation Reason: </span>{viewModal.cancellation_reason}
                </div>
              )}

              <button
                type="button"
                onClick={() => printInvoice(viewModal)}
                className="w-full rounded-full bg-black py-3 font-black text-yellow-300 hover:bg-gray-900 shadow text-sm"
              >
                🖨 Print / Download Invoice
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AccountantDashboard;
